import { defineStore, skipHydrate } from 'pinia'
import type { FormProblemState } from '~/services/forms/FormProblemState'
import type { CartIndexSuccess } from '~/services/api/generated'
import { Normalization } from '~/utils/normalization'
import { gratuityConfig } from '~/config/gratuityConfig'
import { orderMinimumShortfall } from '~/utils/orderMinimumShortfall'
import type { GratuitySelection } from '~/types/GratuitySelection'
import { gratuitySelection } from '~/utils/gratuitySelection'
import { identityGeneration } from '~/utils/identityGeneration'
import { OrderType, FeeType } from '~/enums'

const isRecord = Normalization.isRecord

const DEFAULT_UTENSILS_CHOICE = 'No individual utensils'

interface CartState {
  stores: CartIndexSuccess['data']['stores']
  summary: CartIndexSuccess['data']['summary']
  application_fee: CartIndexSuccess['data']['application_fee']
  delivery_fee: CartIndexSuccess['data']['delivery_fee']
  gratuity: CartIndexSuccess['data']['gratuity']
  loading: boolean
  problem: FormProblemState | null
}

export const useCartStore = defineStore('cart', () => {
  const resolveEventDateTime = (
    value: unknown
  ): { eventDate: string; eventTime: string } | null => {
    if (!isRecord(value)) return null
    const eventTime = value['eventTime']
    const eventDateValue = value['eventDate']
    if (typeof eventTime !== 'string' || eventTime === '') return null

    let eventDate: string | null = null
    if (typeof eventDateValue === 'string') {
      eventDate = eventDateValue
    } else if (eventDateValue instanceof Date && !Number.isNaN(eventDateValue.getTime())) {
      eventDate = [
        eventDateValue.getFullYear(),
        String(eventDateValue.getMonth() + 1).padStart(2, '0'),
        String(eventDateValue.getDate()).padStart(2, '0')
      ].join('-')
    }

    if (eventDate == null || eventDate === '') return null

    return { eventDate, eventTime }
  }

  const getStoreApplicationFee = (
    store: CartIndexSuccess['data']['stores'][number] | null
  ): number => store?.application_fee ?? 0

  const createDefaultCartState = (): CartState => ({
    stores: [],
    summary: {
      subtotal: 0,
      item_count: 0,
      store_count: 0
    },
    application_fee: {
      type: FeeType.PERCENTAGE,
      fixed_amount: 0,
      percentage: 0,
      auto_apply: false
    },
    delivery_fee: {
      type: 'fixed',
      fixed_amount: 0,
      percentage: 0,
      auto_apply: false
    },
    gratuity: {
      dine_in_percentage: 0
    },
    loading: false,
    problem: null
  })

  const cart = ref<CartState>(createDefaultCartState())

  const isDraftOrdersOpen = ref(false)
  const isStoreCartOpen = ref(false)
  const currentStoreSlug = ref<string | null>(null)
  const tipSelection = ref<GratuitySelection>({
    mode: 'percent',
    percent: gratuityConfig.defaultPercent
  })
  const deliveryDate = computed(() => {
    const searchStore = useSearchStore()
    const eventDateTime = resolveEventDateTime(searchStore.eventDateTime)

    if (!eventDateTime) {
      return 'Anytime'
    }

    // Parse the date (format: YYYY-MM-DD)
    const [year, month, day] = eventDateTime.eventDate.split('-').map(Number)
    const date = new Date(year ?? Number.NaN, (month ?? Number.NaN) - 1, day ?? Number.NaN)

    // Format date as "Sat 9/6"
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric'
    })

    // Convert 24-hour time to 12-hour format
    const [hours, minutes] = eventDateTime.eventTime.split(':').map(Number)
    const time = new Date()
    time.setHours(hours ?? Number.NaN, minutes ?? Number.NaN, 0, 0)
    const formattedTime = time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })

    return `${formattedDate}, ${formattedTime}`
  })
  const utensils = ref(DEFAULT_UTENSILS_CHOICE)

  const draftOrders = computed(() => {
    return cart.value.stores.map((store) => ({
      store_slug: store.store_slug,
      store_name: store.store_name,
      item_count: store.item_count,
      subtotal: store.subtotal,
      items: store.items,
      address: store.items?.[0]?.store?.address ?? 'Address not available',
      city: store.items?.[0]?.store?.city ?? '',
      state: store.items?.[0]?.store?.state ?? '',
      zip: store.items?.[0]?.store?.zip ?? ''
    }))
  })

  const draftOrdersSubtotal = computed(() => cart.value.summary.subtotal)
  const draftOrdersItemCount = computed(() => cart.value.summary.item_count)
  const draftOrdersStoreCount = computed(() => cart.value.summary.store_count)
  const draftOrdersCartItemCount = computed(() =>
    cart.value.stores.reduce((total, store) => total + (store.items?.length ?? 0), 0)
  )

  const currentStoreData = computed(() => {
    if (currentStoreSlug.value == null || currentStoreSlug.value === '') {
      return null
    }

    return cart.value.stores.find((store) => store.store_slug === currentStoreSlug.value) ?? null
  })

  const storeSubtotal = computed(() => currentStoreData.value?.subtotal ?? '0.00')
  const minimumOrder = computed(() => currentStoreData.value?.minimum_order ?? 0)
  const minimumShortfall = computed(() =>
    orderMinimumShortfall(storeSubtotal.value, minimumOrder.value)
  )
  const storeItemCount = computed(() => currentStoreData.value?.item_count ?? 0)
  const storeCartItemCount = computed(() => currentStoreData.value?.items?.length ?? 0)
  const storeItems = computed(() => currentStoreData.value?.items ?? [])

  const tax = computed(() => {
    return currentStoreData.value?.sales_tax ?? '0.00'
  })

  const taxPercentage = computed(() => {
    return currentStoreData.value?.sales_tax_percentage ?? 0
  })

  const hasTax = computed(() => {
    const taxAmount = parseFloat(String(currentStoreData.value?.sales_tax ?? '0'))
    return taxAmount > 0
  })

  const applicationFee = computed(() => {
    return getStoreApplicationFee(currentStoreData.value)
  })

  const applicationFeePercentage = computed(() => {
    const fee = cart.value.application_fee
    if (!fee.auto_apply || fee.type !== FeeType.PERCENTAGE) {
      return 0
    }
    return Math.round(fee.percentage * 100)
  })

  const hasApplicationFee = computed(() => {
    return parseFloat(applicationFeeValue.value) > 0
  })

  const deliveryFee = computed(() => {
    const searchStore = useSearchStore()
    if (searchStore.orderType === OrderType.PICKUP || searchStore.orderType === OrderType.DINE_IN) {
      return '0.00'
    }
    return currentStoreData.value?.delivery_fee ?? '0.00'
  })

  const hasDeliveryFee = computed(() => {
    return parseFloat(String(deliveryFee.value)) > 0
  })

  const applicationFeeValue = computed(() => {
    const fee = cart.value.application_fee
    if (!fee.auto_apply) return '0.00'

    const subtotalNum = parseFloat(String(storeSubtotal.value)) || 0

    if (fee.type === FeeType.PERCENTAGE) {
      return (subtotalNum * fee.percentage).toFixed(2)
    }

    return (fee.fixed_amount ?? 0).toFixed(2)
  })

  // Dine-in gratuity percentage from the cart API, as a fraction (e.g. 0.20 for 20%).
  // Sourced per-partner server-side, falling back to the platform default when unset.
  const dineInGratuityPercentage = computed(() => cart.value.gratuity.dine_in_percentage)

  const dineInGratuityPercentageDisplay = computed(() =>
    Math.round(dineInGratuityPercentage.value * 100)
  )

  const tipAmount = computed(() => {
    const searchStore = useSearchStore()
    if (searchStore.orderType === OrderType.DINE_IN) {
      const subtotalNum = parseFloat(String(storeSubtotal.value)) || 0
      return (subtotalNum * dineInGratuityPercentage.value).toFixed(2)
    }
    return gratuitySelection.amountOf(tipSelection.value, storeSubtotal.value).toFixed(2)
  })

  const total = computed(() => {
    const subtotalNum = parseFloat(String(storeSubtotal.value))
    const taxNum = parseFloat(String(tax.value))
    const applicationFeeNum = parseFloat(applicationFeeValue.value)
    const deliveryFeeNum = parseFloat(String(deliveryFee.value))
    const tipNum = parseFloat(String(tipAmount.value))
    return (subtotalNum + taxNum + applicationFeeNum + deliveryFeeNum + tipNum).toFixed(2)
  })

  const setCurrentStore = (storeSlug: string) => {
    currentStoreSlug.value = storeSlug
  }

  let inFlightOperations = { generation: identityGeneration.current(), count: 0 }

  const beginOperation = (generation: number) => {
    if (inFlightOperations.generation !== generation) inFlightOperations = { generation, count: 0 }
    inFlightOperations.count += 1
    cart.value.loading = true
  }

  const endOperation = (generation: number) => {
    if (inFlightOperations.generation !== generation) return
    inFlightOperations.count = Math.max(0, inFlightOperations.count - 1)
    cart.value.loading = inFlightOperations.count > 0
  }

  let latestFetchId = 0

  const claimFetchId = (): number => {
    latestFetchId += 1
    return latestFetchId
  }

  const isLatestFetch = (fetchId: number): boolean => fetchId === latestFetchId

  const applyCartIndex = (response: CartIndexSuccess) => {
    cart.value.stores = response.data.stores
    cart.value.summary = response.data.summary
    cart.value.application_fee = response.data.application_fee
    cart.value.delivery_fee = response.data.delivery_fee
    cart.value.gratuity = response.data.gratuity
  }

  const setProblem = (problem: FormProblemState | null) => {
    cart.value.problem = problem
  }

  const setStoreCartOpen = (open: boolean) => {
    isStoreCartOpen.value = open
  }

  const openDraftOrdersCart = () => {
    isDraftOrdersOpen.value = true
    isStoreCartOpen.value = false
  }

  const closeDraftOrdersCart = () => {
    isDraftOrdersOpen.value = false
  }

  const openStoreCart = () => {
    isStoreCartOpen.value = true
    isDraftOrdersOpen.value = false
  }

  const closeStoreCart = () => {
    isStoreCartOpen.value = false
  }

  // Clears local cart state on identity loss, without a network call; a fresh guest
  // cart is fetched separately once the guest token is regenerated.
  const resetCart = () => {
    cart.value = createDefaultCartState()
    cart.value.loading =
      inFlightOperations.generation === identityGeneration.current() && inFlightOperations.count > 0
    currentStoreSlug.value = null
    isDraftOrdersOpen.value = false
    isStoreCartOpen.value = false
    tipSelection.value = { mode: 'percent', percent: gratuityConfig.defaultPercent }
    utensils.value = DEFAULT_UTENSILS_CHOICE
  }

  return {
    cart: skipHydrate(readonly(cart)),
    loading: computed(() => cart.value.loading),
    problem: computed(() => cart.value.problem),

    draftOrders,
    draftOrdersSubtotal,
    draftOrdersItemCount,
    draftOrdersStoreCount,
    draftOrdersCartItemCount,

    currentStoreSlug: skipHydrate(readonly(currentStoreSlug)),
    currentStoreData,
    storeSubtotal,
    minimumOrder,
    minimumShortfall,
    storeItemCount,
    storeCartItemCount,
    storeItems,

    isDraftOrdersOpen: skipHydrate(readonly(isDraftOrdersOpen)),
    isStoreCartOpen: skipHydrate(readonly(isStoreCartOpen)),
    tipSelection,
    deliveryDate,
    utensils,

    tax,
    taxPercentage,
    hasTax,
    deliveryFee,
    hasDeliveryFee,
    tipAmount,
    total,
    applicationFee,
    applicationFeePercentage,
    applicationFeeValue,
    hasApplicationFee,
    dineInGratuityPercentage,
    dineInGratuityPercentageDisplay,

    setCurrentStore,
    beginOperation,
    endOperation,
    claimFetchId,
    isLatestFetch,
    applyCartIndex,
    setProblem,
    setStoreCartOpen,
    openDraftOrdersCart,
    closeDraftOrdersCart,
    openStoreCart,
    closeStoreCart,
    resetCart
  }
})
